import {
  Box,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import { getPurchaseLineFeatures } from "../../api/documents";
import { useAppTranslation } from "../../hooks/useAppTranslation";
import DocumentPurchaseMatchingSection from "./DocumentPurchaseMatchingSection";
import DocumentPurchaseMatchingState from "./DocumentPurchaseMatchingState";

type MatchingWorkspaceTab = "assign" | "saved";

type Props = {
  documentId: string;
  companyId: string;
  goodsInvoice: boolean;
  currency: string;
  editorRefreshKey: number;
  stateRefreshKey: number;
  onMatchingApplied?: () => void;
  onMatchingChanged?: () => void;
};

export default function DocumentPurchaseMatchingWorkspace({
  documentId,
  companyId,
  goodsInvoice,
  currency,
  editorRefreshKey,
  stateRefreshKey,
  onMatchingApplied,
  onMatchingChanged,
}: Props) {
  const { t } = useAppTranslation("common");
  const [tab, setTab] = useState<MatchingWorkspaceTab>("assign");
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let active = true;

    const loadFeatures = async () => {
      try {
        const features = await getPurchaseLineFeatures(companyId);
        if (active) {
          setEnabled(features.usePurchOrderMatching === true);
        }
      } catch (error) {
        console.error(error);
        if (active) {
          setEnabled(false);
        }
      }
    };

    void loadFeatures();

    return () => {
      active = false;
    };
  }, [companyId]);

  if (!goodsInvoice || !enabled) {
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ mt: 1.5, overflow: "hidden" }}>
      <Box sx={{ px: 2, pt: 2, pb: 1.25 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {t("Dopasowanie ZZ/PZ")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {t(
            "Dopasuj wiersze faktury do zamówień i przyjęć, a następnie kontroluj zapisane przypisania i ilości."
          )}
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, value: MatchingWorkspaceTab) => setTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          px: 1,
          borderTop: 1,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "action.hover",
        }}
      >
        <Tab value="assign" label={t("Dopasuj wiersze")} />
        <Tab value="saved" label={t("Zapisane dopasowania")} />
      </Tabs>

      <Box sx={{ p: 2 }}>
        {tab === "assign" && (
          <DocumentPurchaseMatchingSection
            key={`purchase-matching-editor-${editorRefreshKey}`}
            documentId={documentId}
            companyId={companyId}
            goodsInvoice={goodsInvoice}
            currency={currency}
            embedded
            onMatchingApplied={onMatchingApplied}
          />
        )}

        {tab === "saved" && (
          <DocumentPurchaseMatchingState
            documentId={documentId}
            companyId={companyId}
            goodsInvoice={goodsInvoice}
            refreshKey={stateRefreshKey}
            embedded
            onMatchingChanged={onMatchingChanged}
          />
        )}
      </Box>
    </Paper>
  );
}
